/**
 * PasteLeadsModal — Iter 22 (audit D3).
 *
 * Paste a free-form list of "Name, Email[, Company]" rows and the backend
 * parser figures out the format (commas, tabs, semicolons, with or without
 * a header row). Server returns a preview + skipped count so we can show
 * confirmation before committing.
 *
 * The backend route is POST /api/leads/paste — it does both the parse
 * AND the insert in one shot, returning the created lead IDs and a small
 * preview slice. The audit recommended this over CSV-first because CSV
 * is a developer concept; "paste a list" is a human concept.
 */
import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ClipboardList, Check } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";

export function PasteLeadsModal({ open, onOpenChange, onCreated }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  // Iter 23 (audit D3) — backend's hardened parser now returns
  // `skipped` as a list of {line, reason} objects when individual rows
  // couldn't be parsed. We keep the most recent skips around so the user
  // can correct and re-try. The modal still auto-closes if ≥1 lead landed.
  const [skippedRows, setSkippedRows] = useState([]);
  const reset = () => { setText(""); setSkippedRows([]); };
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      toast.error("Paste at least one Name + Email pair");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/leads/paste", { text });
      const { created, skipped } = r.data || {};
      const skippedList = Array.isArray(skipped) ? skipped : [];
      const skippedSummary = skippedList.length
        ? `, skipped ${skippedList.length} row${skippedList.length === 1 ? "" : "s"}`
        : "";
      if (created > 0) {
        toast.success(`Added ${created} lead${created === 1 ? "" : "s"}${skippedSummary}`);
        reset();
        onCreated?.(r.data);
        onOpenChange?.(false);
      } else {
        // Nothing parsed — keep modal open + show the skipped reasons so
        // the user can fix and re-paste.
        toast.error("Couldn't parse any leads — check the rows below");
        setSkippedRows(skippedList);
      }
    } catch (err) {
      toast.error(errorMessage(err) || "Could not import leads");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-xl" data-testid="paste-leads-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-2xl">
            <ClipboardList className="h-5 w-5 text-violet-300" /> Paste a list of leads
          </DialogTitle>
          <DialogDescription className="text-white/55">
            One per line — "Name, Email" or "Name, Email, Company". Tabs and semicolons work too.
            We'll figure out the rest.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            placeholder={"Jane Doe, jane@acme.com, Acme Inc.\nJohn Smith, john@buzz.io\nAlice Lin\talice@beta.co\tBeta"}
            className="border-white/10 bg-black/40 font-mono text-xs text-white"
            data-testid="paste-leads-textarea"
          />
          <div className="flex items-center gap-2 text-xs text-white/45">
            <Check className="h-3.5 w-3.5 text-emerald-400" />
            We skip blank lines + dedupe by email so you can paste freely.
          </div>

          {/* Iter 23 (audit D3) — surface rows the parser couldn't handle
              when zero leads were created. Helps the user fix + retry. */}
          {skippedRows.length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.05] p-3 text-xs" data-testid="paste-leads-skipped">
              <div className="mb-1.5 font-medium text-amber-200">
                Couldn't parse {skippedRows.length} row{skippedRows.length === 1 ? "" : "s"} —
                fix the lines below and re-paste:
              </div>
              <ul className="space-y-1 text-amber-100/75">
                {skippedRows.slice(0, 8).map((r, i) => (
                  <li key={i} className="font-mono text-[11px]">
                    <span className="text-amber-100">{r.line || "(empty line)"}</span>
                    <span className="text-amber-300/55"> — {r.reason}</span>
                  </li>
                ))}
                {skippedRows.length > 8 && (
                  <li className="text-[11px] italic text-amber-300/55">
                    …and {skippedRows.length - 8} more
                  </li>
                )}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-white/65"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !text.trim()}
              className="rounded-xl bg-white text-black hover:bg-zinc-200"
              data-testid="paste-leads-submit"
            >
              {saving && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Add leads
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
